/**
 * Service Line Detail View
 * Shows detailed information for service lines in a table format with technology logos
 */

import React from 'react';
import { BottomNavigation } from '../../../components/shared/Navigation';
import { NavigationProps } from '../../../types/slideTypes';
import { ServiceCategory } from '../serviceLines';

interface ServiceLineDetailProps extends NavigationProps {
  category: ServiceCategory;
  onBack: () => void;
}

export const ServiceLineDetail: React.FC<ServiceLineDetailProps> = ({
  category,
  onBack,
  currentSlide,
  totalSlides,
  onPrevious,
  onNext,
  onGoToSlide,
}) => {
  return (
    <div className="h-full bg-white flex flex-col relative">
      {/* Main Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Header with Back Button */}
        <div className="bg-slate-50 border-b-4 border-slate-300 px-4 py-4 md:px-6 md:py-5 lg:px-8 lg:py-6">
          <button
            onClick={onBack}
            className="flex items-center space-x-2 text-slate-700 hover:text-slate-900 transition-colors mb-3 md:mb-4 min-h-[44px]"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            <span className="font-medium">Back to Categories</span>
          </button>

          <div className="flex items-center space-x-4">
            <div className="text-5xl">{category.icon}</div>
            <div>
              <h1 className="text-4xl font-bold text-slate-700">
                {category.name}
              </h1>
            </div>
          </div>
        </div>

        {/* Table Content */}
        <div className="p-8">
          <div className="border-2 border-slate-200 rounded-xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-100 border-b-2 border-slate-300">
                  <th className="text-left px-6 py-3 text-slate-800 font-bold">
                    Service Line
                  </th>
                  <th className="text-left px-6 py-3 text-slate-800 font-bold">
                    Solution Skillset
                  </th>
                  <th className="text-left px-6 py-3 text-slate-800 font-bold">
                    Example Leadership Roles
                  </th>
                  <th className="text-left px-6 py-3 text-slate-800 font-bold">
                    Example Contributor Roles
                  </th>
                </tr>
              </thead>
              <tbody>
                {category.serviceLines.map((serviceLine, index) => (
                  <tr
                    key={index}
                    className={`border-b border-slate-200 hover:bg-slate-50 transition-colors ${
                      index % 2 === 0 ? 'bg-white' : 'bg-slate-50'
                    }`}
                  >
                    {/* Service Line Name */}
                    <td className="px-6 py-3 align-top">
                      <div className="font-bold text-slate-900 text-lg">
                        {serviceLine.name}
                      </div>
                    </td>

                    {/* Solution Skillset with Logos */}
                    <td className="px-6 py-3 align-top">
                      <div className="flex flex-wrap gap-1.5">
                        {serviceLine.skillsets.map((skill, idx) => {
                          const logoInfo = getTechnologyLogo(skill);
                          return (
                            <div
                              key={idx}
                              className="inline-flex items-center bg-slate-100 border border-slate-200 px-3 py-1.5 rounded-lg text-sm"
                            >
                              {logoInfo.hasLogo ? (
                                <img
                                  src={logoInfo.logoUrl}
                                  alt={skill}
                                  className="w-6 h-6 mr-2"
                                  onError={(e) => {
                                    // Hide image on error and show text only
                                    e.currentTarget.style.display = 'none';
                                  }}
                                />
                              ) : null}
                              <span className="text-slate-700 font-medium">{skill}</span>
                            </div>
                          );
                        })}
                      </div>
                    </td>

                    {/* Leadership Roles */}
                    <td className="px-6 py-3 align-top">
                      <ul className="space-y-1.5 text-xs text-slate-700">
                        {serviceLine.leadershipRoles.map((role, idx) => (
                          <li key={idx} className="flex items-start">
                            <span className="text-slate-400 mr-2">•</span>
                            <span>{role}</span>
                          </li>
                        ))}
                      </ul>
                    </td>

                    {/* Contributor Roles */}
                    <td className="px-6 py-3 align-top">
                      <ul className="space-y-1.5 text-xs text-slate-700">
                        {serviceLine.contributorRoles.map((role, idx) => (
                          <li key={idx} className="flex items-start">
                            <span className="text-slate-400 mr-2">•</span>
                            <span>{role}</span>
                          </li>
                        ))}
                      </ul>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Bottom Navigation */}
      <BottomNavigation
        currentSlide={currentSlide}
        totalSlides={totalSlides}
        onPrevious={onPrevious}
        onNext={onNext}
        onGoToSlide={onGoToSlide}
      />
    </div>
  );
};

/**
 * Get technology logo from Simple Icons CDN
 * Returns logo URL and whether logo is available
 */
function getTechnologyLogo(skillName: string): { hasLogo: boolean; logoUrl: string } {
  // Map of technology names to Simple Icons slugs
  const logoMap: Record<string, string> = {
    // Cloud Platforms
    'AWS': 'amazonaws',
    'Amazon Web Services': 'amazonaws',
    'AWS SageMaker': 'amazonaws',
    'AWS API Gateway': 'amazonaws',
    'Azure': 'microsoftazure',
    'Microsoft Azure': 'microsoftazure',
    'Azure DevOps': 'azuredevops',
    'Azure AI': 'microsoftazure',
    'Azure Logic Apps': 'microsoftazure',
    'Google Cloud Platform': 'googlecloud',
    'Google Cloud': 'googlecloud',
    'Google Vertex AI': 'googlecloud',
    'GCP': 'googlecloud',

    // ERP & Enterprise
    'SAP': 'sap',
    'SAP SuccessFactors': 'sap',
    'SAP IBP': 'sap',
    'SAP EWM': 'sap',
    'SAP Commerce': 'sap',
    'SAP MDM': 'sap',
    'Oracle': 'oracle',
    'Oracle CX': 'oracle',
    'Oracle HCM': 'oracle',
    'Oracle SCM Cloud': 'oracle',
    'Oracle WMS': 'oracle',
    'Oracle Hyperion': 'oracle',
    'Microsoft Dynamics': 'dynamics365',
    'Microsoft Dynamics CRM': 'dynamics365',
    'Dynamics': 'dynamics365',
    'NetSuite': 'netsuite',
    'Workday': 'workday',
    'Anaplan': 'anaplan',
    'ADP': 'adp',

    // CRM
    'Salesforce': 'salesforce',
    'Salesforce Commerce Cloud': 'salesforce',
    'HubSpot': 'hubspot',

    // Data & Analytics
    'Snowflake': 'snowflake',
    'Databricks': 'databricks',
    'Tableau': 'tableau',
    'Power BI': 'powerbi',
    'Microsoft Fabric': 'microsoft',
    'Informatica': 'informatica',
    'Informatica MDM': 'informatica',
    'Palantir': 'palantir',
    'Talend': 'talend',

    // AI/ML
    'OpenAI': 'openai',
    'Anthropic': 'anthropic',
    'TensorFlow': 'tensorflow',
    'PyTorch': 'pytorch',

    // DevOps & Tools
    'Jenkins': 'jenkins',
    'GitLab': 'gitlab',
    'GitHub': 'github',
    'GitHub Actions': 'githubactions',
    'Docker': 'docker',
    'Kubernetes': 'kubernetes',
    'Terraform': 'terraform',
    'Ansible': 'ansible',

    // Integration & Middleware
    'Boomi': 'boomi',
    'MuleSoft': 'mulesoft',

    // Development
    'React': 'react',
    'Node.js': 'nodedotjs',
    'Python': 'python',
    'Java': 'openjdk',
    'Java/Spring': 'spring',
    'Spring': 'spring',
    'JavaScript': 'javascript',
    '.NET': 'dotnet',
    'Microsoft .NET': 'dotnet',

    // Collaboration
    'Jira': 'jira',
    'Atlassian': 'atlassian',
    'Confluence': 'confluence',
    'Slack': 'slack',
    'Teams': 'microsoftteams',
    'Microsoft Teams': 'microsoftteams',
    'SharePoint': 'microsoftsharepoint',
    'Microsoft 365': 'microsoft365',
    'Microsoft 365/SharePoint': 'microsoft365',
    'Microsoft Project': 'microsoftproject',
    'Microsoft Viva': 'microsoftteams',

    // Testing
    'Selenium': 'selenium',
    'Katalon': 'katalon',

    // Supply Chain
    'Manhattan Associates': 'manhattan',
    'Manhattan Associates (WMS)': 'manhattan',
    'Blue Yonder': 'blueyonder',
    'Kinaxis': 'kinaxis',
    'Kinaxis RapidResponse': 'kinaxis',

    // E-Commerce
    'Shopify': 'shopify',
    'Shopify Plus': 'shopify',
    'Magento': 'magento',
    'Adobe Commerce': 'adobe',
    'Adobe': 'adobe',

    // Project Management & Training
    'Smartsheet': 'smartsheet',
    'ServiceNow': 'servicenow',
    'ServiceNow PPM': 'servicenow',
    'Planview': 'planview',
    'WalkMe': 'walkme',
    'Whatfix': 'whatfix',
    'Pendo': 'pendo',
  };

  // Try to find a match
  let slug = '';
  for (const [key, value] of Object.entries(logoMap)) {
    if (skillName.toLowerCase().includes(key.toLowerCase())) {
      slug = value;
      break;
    }
  }

  if (slug) {
    // Return Simple Icons CDN URL (using slate-700 color to match theme)
    return {
      hasLogo: true,
      logoUrl: `https://cdn.simpleicons.org/${slug}/475569`, // 475569 is slate-700 hex
    };
  }

  return { hasLogo: false, logoUrl: '' };
}
